import { useState, useEffect, useCallback } from 'react';
import { authHeaders, Toast } from './listPageStyles';
import { ListPageShell } from './ui';
import { usePageMeta } from '../hooks/usePageMeta';

// Rozetler / Başarımlar — mobil "Satış Maratonu" ekranındaki rozet sisteminin web kataloğu.
// Rozetler sahada cihazda hesaplanır (skor entity yok): ilgili kategoride herhangi bir
// hedefin %80'ine ulaşan temsilci rozeti kazanır. Bu ekran rozet tanımlarını gösterir +
// dönemin şampiyonlarını mevcut /api/leaderboard verisinden listeler.
const API_BASE = '/api/leaderboard';

// Mobil LeaderboardViewModel.badgesFrom() ile birebir: 3 rozet, 3 hedef kategorisi.
const BADGES = [
  {
    key: 'SATIS',
    name: 'Satış Yıldızı',
    emoji: '⭐',
    grad: 'linear-gradient(135deg,#f7d774,#e0a106)',
    category: 'Satış',
    criteria: 'Satış kategorisindeki herhangi bir hedefin %80’ine ulaşıldığında kazanılır.',
  },
  {
    key: 'ZIYARET',
    name: 'Ziyaret Şampiyonu',
    emoji: '🚀',
    grad: 'linear-gradient(135deg,#5eead4,#0d9488)',
    category: 'Ziyaret',
    criteria: 'Ziyaret kategorisindeki herhangi bir hedefin %80’ine ulaşıldığında kazanılır.',
  },
  {
    key: 'FKMS',
    name: 'Bulunurluk Ustası',
    emoji: '🏅',
    grad: 'linear-gradient(135deg,#c4b5fd,#7c3aed)',
    category: 'FKMS',
    criteria: 'FKMS (bulunurluk) kategorisindeki herhangi bir hedefin %80’ine ulaşıldığında kazanılır.',
  },
];

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}

function unwrapList(payload) {
  const data = pick(payload, 'data', 'Data');
  if (Array.isArray(data)) return data;
  const items = pick(data, 'items', 'Items');
  return Array.isArray(items) ? items : [];
}

const fmtMoney = (v) =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(v || 0);
const initial = (name) => (name || '—').trim().charAt(0).toLocaleUpperCase('tr-TR') || '—';

const CHAMP_GRAD = [
  'linear-gradient(135deg,#f7d774,#e0a106)',
  'linear-gradient(135deg,#e7eaf0,#aab2c0)',
  'linear-gradient(135deg,#e7b07a,#b9712f)',
];

function BadgeCard({ b }) {
  return (
    <div
      style={{
        border: '1px solid var(--border-color,#e5e7eb)', borderRadius: 14, padding: 18,
        background: 'var(--bg-card,#fff)', display: 'flex', flexDirection: 'column', gap: 10,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div
          style={{
            width: 52, height: 52, borderRadius: '50%', background: b.grad,
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24,
            boxShadow: '0 4px 12px rgba(0,0,0,.15)',
          }}
        >
          {b.emoji}
        </div>
        <div>
          <div style={{ fontWeight: 800, fontSize: 15, color: 'var(--text-primary,#111827)' }}>{b.name}</div>
          <span
            style={{
              display: 'inline-block', marginTop: 4, fontSize: 11, fontWeight: 700,
              padding: '2px 8px', borderRadius: 999, background: 'var(--grid-canvas,#eef2f9)',
              color: 'var(--text-secondary,#4b5563)',
            }}
          >
            {b.category}
          </span>
        </div>
      </div>
      <div style={{ fontSize: 12.5, lineHeight: 1.5, color: 'var(--text-secondary,#4b5563)' }}>{b.criteria}</div>
    </div>
  );
}

function ChampionRow({ e, idx }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--border-color,#e5e7eb)' }}>
      <div
        style={{
          width: 40, height: 40, borderRadius: '50%', background: CHAMP_GRAD[idx],
          display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800,
        }}
      >
        {initial(e.name)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, color: 'var(--text-primary,#111827)' }}>{e.name}</div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary,#4b5563)' }}>{idx + 1}. sıra · {e.points} puan</div>
      </div>
      <div style={{ fontWeight: 800, color: '#5b3fb0' }}>{fmtMoney(e.totalSales)}</div>
    </div>
  );
}

export function GamificationBadgesPage() {
  usePageMeta('Rozetler & Başarımlar', 'Satış Maratonu rozet kataloğu ve dönemin şampiyonları.');
  const [champions, setChampions] = useState([]);
  const [toast, setToast] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}?period=month`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Şampiyonlar alınamadı (${res.status})`);
      const payload = await res.json();
      const items = unwrapList(payload).slice(0, 3).map((r) => ({
        name: pick(r, 'name', 'Name') ?? '—',
        totalSales: Number(pick(r, 'totalSales', 'TotalSales') ?? 0),
        points: Number(pick(r, 'points', 'Points') ?? 0),
      }));
      setChampions(items);
    } catch (err) {
      setToast({ type: 'error', message: err.message || 'Şampiyonlar alınamadı' });
      setChampions([]);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { if (toast) { const t = setTimeout(() => setToast(null), 3500); return () => clearTimeout(t); } }, [toast]);

  return (
    <>
      <Toast toast={toast} />
      <ListPageShell
        title="Rozetler & Başarımlar"
        subtitle="Satış Maratonu rozet sistemi — saha temsilcileri hedeflerini tamamladıkça mobilde kazanır."
      >
        <div style={{ padding: 4 }}>
          <div
            style={{
              background: 'linear-gradient(135deg,#3f2d8c,#7c4fd0)', borderRadius: 16, padding: '18px 20px',
              color: '#fff', marginBottom: 18,
            }}
          >
            <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 4 }}>🎯 Satış Maratonu Rozetleri</div>
            <div style={{ fontSize: 13, opacity: 0.92, lineHeight: 1.5 }}>
              Temsilciler ilgili hedef kategorisinin %80’ine ulaştığında rozeti kazanır. Rozetler mobil uygulamadaki
              Satış Maratonu ekranında “Rozetlerim” bölümünde görünür.
            </div>
          </div>

          <div
            style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 14, marginBottom: 22,
            }}
          >
            {BADGES.map((b) => <BadgeCard key={b.key} b={b} />)}
          </div>

          <div style={{ border: '1px solid var(--border-color,#e5e7eb)', borderRadius: 14, padding: 18, background: 'var(--bg-card,#fff)' }}>
            <div style={{ fontWeight: 800, fontSize: 14, color: 'var(--text-primary,#111827)', marginBottom: 6 }}>
              🏆 Bu Ayın Şampiyonları
            </div>
            {champions.length === 0 ? (
              <div style={{ padding: '24px 0', color: 'var(--text-secondary,#4b5563)', fontWeight: 600, fontSize: 13 }}>
                Bu dönem için sıralama verisi yok.
              </div>
            ) : (
              champions.map((e, i) => <ChampionRow key={e.name + i} e={e} idx={i} />)
            )}
          </div>
        </div>
      </ListPageShell>
    </>
  );
}

export default GamificationBadgesPage;
